import { useAppVersionStore } from "./use-app-version-store";

const RECOVERY_KEY = "app-version:recovered-at";
const RECOVERY_WINDOW_MS = 30_000;

/**
 * Whether a recovery reload already happened inside the window. Pure so the
 * window arithmetic is testable without a DOM — the caller supplies the raw
 * stored value and the clock.
 */
export function hasRecoveredRecently(raw: string | null, now: number): boolean {
  const recoveredAt = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(recoveredAt) && now - recoveredAt < RECOVERY_WINDOW_MS;
}

/**
 * The impersonation frame (`bootImpersonationFrame`) can be sandboxed, where
 * touching sessionStorage throws. A recovery attempt must never be the thing
 * that breaks the page, so a blocked store degrades to "nothing recorded".
 */
function readRecoveredAt(): string | null {
  try {
    return sessionStorage.getItem(RECOVERY_KEY);
  } catch {
    return null;
  }
}

function writeRecoveredAt(now: number): void {
  try {
    sessionStorage.setItem(RECOVERY_KEY, String(now));
  } catch {
    // Without the marker the window guard is lost, but `acquireRefresh` still
    // bounds this document to one reload — good enough to avoid a loop.
  }
}

/**
 * Replace the document to pick up the newer deployment, at most once.
 *
 * Replacing the document IS the mechanism. The chunk that 404'd is gone for
 * good, so nothing retries its URL — instead the browser re-fetches
 * `index.html`, which is the thing that names the hashes, and the new document
 * boots on the new build's chunks. Fetching the replacement chunk into the
 * running page is not an option: a route chunk statically imports ~700 hashed
 * siblings including React and React-DOM, so it would load a second copy of the
 * whole app in the same realm.
 *
 * Returns true when this call owns the reload. False means recovery is spent —
 * either another path already took it or one happened inside the window — and
 * the caller must surface the real failure instead of trying again, so a
 * genuinely broken chunk cannot loop the page.
 */
export function reloadForStaleChunk(href: string): boolean {
  const now = Date.now();
  if (hasRecoveredRecently(readRecoveredAt(), now)) return false;
  if (!useAppVersionStore.getState().actions.acquireRefresh()) return false;

  writeRecoveredAt(now);
  // `href` may be a router location, which carries no origin — resolve both
  // sides before comparing or the check never matches.
  const target = new URL(href, window.location.href);
  if (target.href === window.location.href) {
    // Assigning the URL we are already on is a same-document fragment
    // navigation whenever the URL carries a fragment (HTML navigate step 15 —
    // the fragment does not have to differ), which would return true here
    // having done nothing, with the latch already spent. `reload` is defined as
    // a reload, so it always re-fetches, and it bypasses the HTTP cache.
    window.location.reload();
  } else {
    window.location.assign(target);
  }
  return true;
}
